import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate, useParams } from "react-router-dom";
import { MathJaxContext } from "better-react-mathjax";
import {
  FaChevronDown,
  FaChevronRight,
  FaTimes,
  FaChevronLeft,
  FaShareAlt,
  FaQuestionCircle,
  FaImage,
  FaChevronRight as FaChevronRightIcon,
} from "react-icons/fa";
import { toast } from "react-toastify";
import SimpleThreeDotsLoader from "../components/SimpleThreeDotsLoader";
import MathJaxRenderer from "../components/MathJaxRenderer";
import DoubtSolver from "../components/DoubtSolver";

const mathJaxConfig = {
  tex: {
    inlineMath: [
      ["$", "$"],
      ["\\(", "\\)"],
    ],
    displayMath: [
      ["$$", "$$"],
      ["\\[", "\\]"],
    ],
    processEscapes: true,
    processEnvironments: true,
    packages: ["base", "ams", "mhchem"],
  },
  options: {
    enableMenu: false,
  },
};

const applyBasicMarkdown = (text) => {
  if (!text) return text;
  try {
    return text.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>");
  } catch {
    return text;
  }
};

const pickFirstNonEmptyString = (...values) => {
  for (const value of values) {
    if (typeof value === "string") {
      const trimmed = value.trim();
      if (trimmed.length > 0) {
        return trimmed;
      }
    }
  }
  return null;
};

const normalizeUrlList = (items) => {
  if (!Array.isArray(items)) return [];
  return items
    .map((item) => {
      if (typeof item === "string") {
        const trimmed = item.trim();
        return trimmed.length > 0 ? trimmed : null;
      }
      if (item && typeof item === "object") {
        const candidate =
          item.url ||
          item.src ||
          item.imageUrl ||
          item.image_url ||
          item.path ||
          item.fileUrl ||
          item.file_url;
        if (typeof candidate === "string") {
          const normalized = candidate.trim();
          if (normalized.length > 0) {
            return normalized;
          }
        }
      }
      return null;
    })
    .filter(Boolean);
};

const decodeAnswerImagesPayload = (items = []) => {
  const decoded = {
    pdfDataUris: [],
    imageEntries: [],
  };

  if (!Array.isArray(items)) {
    return decoded;
  }

  items.forEach((item = {}, index) => {
    const contentType =
      typeof item?.content_type === "string"
        ? item.content_type.trim()
        : typeof item?.mimeType === "string"
        ? item.mimeType.trim()
        : typeof item?.type === "string"
        ? item.type.trim()
        : "";
    const rawData =
      typeof item?.data === "string" && item.data.trim().length > 0
        ? item.data.trim()
        : "";
    if (!rawData) {
      return;
    }

    const dataUri = rawData.startsWith("data:")
      ? rawData
      : `data:${contentType || "application/octet-stream"};base64,${rawData}`;
    const label =
      (typeof item?.filename === "string" && item.filename.trim().length > 0
        ? item.filename.trim()
        : typeof item?.name === "string" && item.name.trim().length > 0
        ? item.name.trim()
        : `Answer file ${index + 1}`) || `Answer file ${index + 1}`;

    const fileType =
      typeof item?.file_type === "string"
        ? item.file_type.toLowerCase()
        : typeof item?.type === "string"
        ? item.type.toLowerCase()
        : "";

    if (
      (contentType && contentType.toLowerCase().includes("pdf")) ||
      fileType === "pdf"
    ) {
      decoded.pdfDataUris.push(dataUri);
    } else {
      decoded.imageEntries.push({ src: dataUri, label });
    }
  });

  return decoded;
};

const normalizeWorksheetAssets = (payload = {}) => {
  const data = payload?.data ?? payload ?? {};
  const pdfUrl = pickFirstNonEmptyString(
    data.pdfUrl,
    data.pdf_url,
    data.pdf,
    data.pdfLink,
    data.pdf_link,
    data.answersPdfUrl,
    data.answerPdfUrl,
    data.fileUrl,
    data.file_url
  );

  const answerImagesPayload = Array.isArray(data.answerImages)
    ? data.answerImages
    : Array.isArray(payload?.answerImages)
    ? payload.answerImages
    : [];
  const decodedAnswerImages = decodeAnswerImagesPayload(answerImagesPayload);

  const possibleCollections = [
    data.images,
    data.imageUrls,
    data.image_urls,
    data.answerImages,
    data.answer_images,
    data.pages,
    data.files,
    data.fileUrls,
    data.file_urls,
  ];

  let imageUrls = decodedAnswerImages.imageEntries.slice();
  for (const collection of possibleCollections) {
    const urls = normalizeUrlList(collection);
    if (urls.length > 0) {
      const wrapped = urls.map((url, index) => ({
        src: url,
        label: `Worksheet page ${index + 1}`,
      }));
      if (wrapped.length > 0) {
        imageUrls =
          imageUrls.length > 0 ? imageUrls.concat(wrapped) : wrapped.slice();
        break;
      }
      break;
    }
  }

  const chosenPdfUrl = decodedAnswerImages.pdfDataUris[0] || pdfUrl || null;

  return { pdfUrl: chosenPdfUrl, imageUrls };
};
const FeedbackContent = ({ row, onAskDoubt }) => {
  const [expanded, setExpanded] = useState(false);
  const [showToggle, setShowToggle] = useState(false);
  const [preview, setPreview] = useState("");
  const containerRef = useRef(null);
  const contentRef = useRef(null);

  const feedback = row?.feedback || "-";
  const marks = row?.marks ?? 0;
  const maxMarks = row?.maxMarks ?? 0;

  const getLineHeight = (el) => {
    const cs = window.getComputedStyle(el);
    let lh = parseFloat(cs.lineHeight);
    if (!lh || Number.isNaN(lh)) {
      const fs = parseFloat(cs.fontSize) || 16;
      lh = fs * 1.2;
    }
    return lh;
  };

  const countVisualLines = (el) => {
    const range = document.createRange();
    range.selectNodeContents(el);
    const rects = Array.from(range.getClientRects()).filter(
      (r) => r.width > 0 && r.height > 0
    );
    if (!rects.length) return 0;

    const lh = getLineHeight(el);
    const THRESH = Math.max(2, lh * 0.4);
    const MIN_HEIGHT = Math.max(1, lh * 0.35);

    rects.sort((a, b) => a.top - b.top);

    let lines = 0,
      currentTop = null;
    for (const r of rects) {
      if (r.height < MIN_HEIGHT) continue;
      if (currentTop === null || Math.abs(r.top - currentTop) > THRESH) {
        lines += 1;
        currentTop = r.top;
      }
    }
    return lines;
  };

  const estimateCharsPerLine = (el) => {
    const cs = window.getComputedStyle(el);
    const font = `${cs.fontStyle} ${cs.fontVariant} ${cs.fontWeight} ${cs.fontSize} / ${cs.lineHeight} ${cs.fontFamily}`;
    const width = el.clientWidth || 0;

    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    ctx.font = font;

    const sample =
      "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ 0123456789";
    const w = ctx.measureText(sample).width || 1;
    const avgChar = w / sample.length;
    if (avgChar <= 0) return 50;
    return Math.max(10, Math.floor(width / avgChar));
  };

  const makePreview = (sourceEl, text) => {
    if (!sourceEl) return "";
    const clean = String(text).replace(/\s+/g, " ").trim();
    const cpl = estimateCharsPerLine(sourceEl);
    const maxChars = Math.max(20, Math.floor(cpl * 2 * 0.95));
    if (clean.length <= maxChars) return clean;
    let cut = clean.slice(0, maxChars);
    const lastSpace = cut.lastIndexOf(" ");
    if (lastSpace > 0) cut = cut.slice(0, lastSpace);
    return cut.trim() + ".";
  };

  useEffect(() => {
    const measure = () => {
      const content = contentRef.current;
      const container = containerRef.current;
      if (!content || !container) return;
      const prevDisplay = content.style.display;
      content.style.display = "block";
      requestAnimationFrame(() => {
        const lines = countVisualLines(content);
        setShowToggle(lines > 2);
        setPreview(makePreview(container, content.textContent || ""));
        content.style.display = prevDisplay || "";
      });
    };

    measure();

    const ro = new ResizeObserver(() => measure());
    if (containerRef.current) ro.observe(containerRef.current);

    const mo = new MutationObserver(() => measure());
    if (contentRef.current)
      mo.observe(contentRef.current, {
        childList: true,
        subtree: true,
        characterData: true,
      });

    const onWinResize = () => measure();
    window.addEventListener("resize", onWinResize);

    return () => {
      ro.disconnect();
      mo.disconnect();
      window.removeEventListener("resize", onWinResize);
    };
  }, [feedback]);

  return (
    <>
      {feedback && feedback !== "-" ? (
        <>
          <div ref={containerRef} style={{ width: "100%", minWidth: 0 }}>
            <div ref={contentRef} className="text-gray-700">
              <MathJaxRenderer>{applyBasicMarkdown(feedback)}</MathJaxRenderer>
            </div>
          </div>
        </>
      ) : (
        <span>-</span>
      )}

      {marks < maxMarks && typeof onAskDoubt === "function" && (
        <div>
          <button
            className="inline-flex items-center gap-1 text-sm font-medium text-indigo-600 hover:text-indigo-700 underline-offset-2 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-600 rounded-sm"
            onClick={onAskDoubt}
          >
            <FaQuestionCircle className="h-4 w-4" aria-hidden />
            Why did I lose marks?
          </button>
        </div>
      )}
    </>
  );
};
const normalizeStepBreakdown = (rawSteps = []) => {
  if (!Array.isArray(rawSteps)) {
    return [];
  }

  return rawSteps.map((step = {}, index) => ({
    ...step,
    step: step?.step ?? index + 1,
    feedback: step?.feedback ?? "",
    marks: typeof step?.marks === "number" ? step.marks : 0,
    maxMarks: typeof step?.maxMarks === "number" ? step.maxMarks : 0,
  }));
};

const normalizeQuestion = (question = {}, index) => {
  const stepBreakdown = normalizeStepBreakdown(question?.stepBreakdown);
  const inferredTotalMarks = stepBreakdown.reduce(
    (acc, step) =>
      acc + (typeof step.maxMarks === "number" ? step.maxMarks : 0),
    0
  );

  const totalMarks =
    typeof question?.totalMarks === "number"
      ? question.totalMarks
      : typeof question?.maxMarks === "number"
      ? question.maxMarks
      : inferredTotalMarks;

  const answerImages = Array.isArray(question?.answerImages)
    ? question.answerImages
    : [];

  return {
    ...question,
    id: question?.id ?? index + 1,
    question_text: question?.question_text ?? "",
    answer_text: question?.answer_text ?? "",
    feedback: question?.feedback ?? "",
    marks: typeof question?.marks === "number" ? question.marks : 0,
    totalMarks: totalMarks || 0,
    stepBreakdown,
    answerImages,
    questionImage: question?.questionImage ?? null,
  };
};

const normalizeSheet = (sheet = {}) => {
  const questions = Array.isArray(sheet?.questions)
    ? sheet.questions.map((question, index) =>
        normalizeQuestion(question, index)
      )
    : [];

  const totalMarks =
    typeof sheet?.totalMarks === "number"
      ? sheet.totalMarks
      : questions.reduce(
          (acc, question) =>
            acc +
            (typeof question.totalMarks === "number" ? question.totalMarks : 0),
          0
        );

  const overallMarks =
    typeof sheet?.overallMarks === "number"
      ? sheet.overallMarks
      : questions.reduce(
          (acc, question) =>
            acc + (typeof question.marks === "number" ? question.marks : 0),
          0
        );

  return {
    ...sheet,
    id: sheet?.id ?? null,
    fileName: sheet?.fileName ?? "Worksheet Evaluation",
    subject: sheet?.subject ?? "",
    overallMarks,
    totalMarks,
    date: sheet?.date ?? "",
    status: sheet?.status ?? "",
    requestId: sheet?.requestId ?? "",
    questions,
  };
};
const PublicEvaluateAnswerSheet = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const baseUrl = import.meta.env.VITE_APP_BASE_URL || "";
  const trimmedBaseUrl = baseUrl.replace(/\/+$/, "");
  const evaluationEndpoint = trimmedBaseUrl
    ? `${trimmedBaseUrl}/v2/worksheetEvaluation/evaluateAnswerSheet`
    : "";
  const worksheetEvaluationStatusEndpointBase = trimmedBaseUrl
    ? `${trimmedBaseUrl}/v2/worksheetEvaluation/status/`
    : "";

  const [evaluation, setEvaluation] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [expandedQuestions, setExpandedQuestions] = useState(new Set());
  const [fetchVersion, setFetchVersion] = useState(0);

  const [leftPanelViewer, setLeftPanelViewer] = useState({
    pdfUrl: null,
    imageUrls: [],
  });
  const [isLeftPanelViewerLoading, setIsLeftPanelViewerLoading] =
    useState(false);
  const [leftPanelViewerError, setLeftPanelViewerError] = useState(null);
  const leftPanelViewerAbortControllerRef = useRef(null);

  const [leftPanelWidth, setLeftPanelWidth] = useState(30);
  const [isLeftPanelResizing, setIsLeftPanelResizing] = useState(false);
  const layoutRef = useRef(null);
  const centerLayoutRef = useRef(null);
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth < 1024 : false
  );

  const [askDoubtText, setAskDoubtText] = useState("");
  const [showSidePanel, setShowSidePanel] = useState(false);
  const [centerPanelWidth, setCenterPanelWidth] = useState(70);
  const [isRightPanelResizing, setIsRightPanelResizing] = useState(false);

  const [showImagePreview, setShowImagePreview] = useState(false);
  const [previewImages, setPreviewImages] = useState([]);
  const [previewIndex, setPreviewIndex] = useState(0);
  const [previewImageAlt, setPreviewImageAlt] = useState("");

  const showWorksheetPreviewPanel = !isMobile && !showSidePanel;

  useEffect(() => {
    const handleResize = () => {
      if (typeof window === "undefined") {
        return;
      }
      setIsMobile(window.innerWidth < 1024);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (isMobile && showSidePanel) {
      setShowSidePanel(false);
      setIsRightPanelResizing(false);
      setAskDoubtText("");
    }
  }, [isMobile, showSidePanel]);

  useEffect(() => {
    let isMounted = true;
    const controller = new AbortController();

    const load = async () => {
      if (!id) {
        setEvaluation(null);
        setError("Missing answer sheet identifier.");
        setIsLoading(false);
        return;
      }

      if (!evaluationEndpoint) {
        setEvaluation(null);
        setError("API base URL is not configured.");
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setError("");

      try {
        const url = `${evaluationEndpoint}/${encodeURIComponent(id)}`;
        const response = await fetch(url, {
          method: "GET",
          headers: {
            Accept: "application/json",
          },
          signal: controller.signal,
        });

        if (!response.ok) {
          throw new Error(
            `Unable to load evaluation result (${response.status})`
          );
        }

        let payload;
        try {
          payload = await response.json();
        } catch (parseError) {
          throw new Error("Received an unexpected response from the server.");
        }

        if (!isMounted) {
          return;
        }

        const normalized = normalizeSheet(payload?.result || {});
        setEvaluation(normalized);

        if (normalized.questions.length > 0) {
          const firstQuestionId = normalized.questions[0]?.id ?? 1;
          setExpandedQuestions(new Set([firstQuestionId]));
        } else {
          setExpandedQuestions(new Set());
        }
      } catch (fetchError) {
        if (controller.signal.aborted) {
          return;
        }
        console.error("Failed to fetch evaluation result", fetchError);
        setEvaluation(null);
        setError(
          fetchError?.message || "Unable to load evaluation result right now."
        );
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    load();

    return () => {
      isMounted = false;
      controller.abort();
    };
  }, [evaluationEndpoint, id, fetchVersion]);

  useEffect(() => {
    if (!isLeftPanelResizing) {
      return;
    }

    const handleMouseMove = (event) => {
      if (!layoutRef.current) {
        return;
      }
      const rect = layoutRef.current.getBoundingClientRect();
      if (rect.width <= 0) {
        return;
      }
      const percentage = ((event.clientX - rect.left) / rect.width) * 100;
      const clamped = Math.min(45, Math.max(15, percentage));
      setLeftPanelWidth(clamped);
    };

    const handleMouseUp = () => {
      setIsLeftPanelResizing(false);
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isLeftPanelResizing]);

  useEffect(() => {
    if (isMobile && isLeftPanelResizing) {
      setIsLeftPanelResizing(false);
    }
  }, [isMobile, isLeftPanelResizing]);

  useEffect(() => {
    if (!isRightPanelResizing) {
      return;
    }
    const handleMouseMove = (event) => {
      if (!centerLayoutRef.current) {
        return;
      }
      const rect = centerLayoutRef.current.getBoundingClientRect();
      if (rect.width <= 0) {
        return;
      }
      const percent = ((event.clientX - rect.left) / rect.width) * 100;
      const clamped = Math.min(85, Math.max(35, percent));
      setCenterPanelWidth(clamped);
    };

    const handleMouseUp = () => {
      setIsRightPanelResizing(false);
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isRightPanelResizing]);

  useEffect(() => {
    if ((!showSidePanel || isMobile) && isRightPanelResizing) {
      setIsRightPanelResizing(false);
    }
  }, [showSidePanel, isMobile, isRightPanelResizing]);

  useEffect(() => {
    if (!showWorksheetPreviewPanel) {
      if (leftPanelViewerAbortControllerRef.current) {
        leftPanelViewerAbortControllerRef.current.abort();
        leftPanelViewerAbortControllerRef.current = null;
      }
      setLeftPanelViewer({ pdfUrl: null, imageUrls: [] });
      setLeftPanelViewerError(null);
      setIsLeftPanelViewerLoading(false);
      return;
    }

    const requestId =
      typeof evaluation?.requestId === "string" &&
      evaluation.requestId.trim().length > 0
        ? evaluation.requestId.trim()
        : null;

    if (!requestId || !worksheetEvaluationStatusEndpointBase) {
      if (leftPanelViewerAbortControllerRef.current) {
        leftPanelViewerAbortControllerRef.current.abort();
        leftPanelViewerAbortControllerRef.current = null;
      }
      setLeftPanelViewer({ pdfUrl: null, imageUrls: [] });
      setLeftPanelViewerError(null);
      setIsLeftPanelViewerLoading(false);
      return;
    }

    const controller = new AbortController();
    if (leftPanelViewerAbortControllerRef.current) {
      leftPanelViewerAbortControllerRef.current.abort();
    }
    leftPanelViewerAbortControllerRef.current = controller;

    setIsLeftPanelViewerLoading(true);
    setLeftPanelViewerError(null);

    const fetchAssets = async () => {
      try {
        const response = await fetch(
          `${worksheetEvaluationStatusEndpointBase}${encodeURIComponent(
            requestId
          )}`,
          {
            method: "GET",
            headers: {
              Accept: "application/json",
            },
            signal: controller.signal,
          }
        );

        if (!response.ok) {
          throw new Error(`Failed to load worksheet file (${response.status})`);
        }

        const payload = await response.json();
        if (controller.signal.aborted) {
          return;
        }

        const normalizedAssets = normalizeWorksheetAssets(payload);
        setLeftPanelViewer(normalizedAssets);
        setLeftPanelViewerError(null);
      } catch (viewerError) {
        if (controller.signal.aborted) {
          return;
        }
        console.error("Unable to fetch worksheet file:", viewerError);
        setLeftPanelViewer({ pdfUrl: null, imageUrls: [] });
        setLeftPanelViewerError(
          viewerError?.message || "Unable to load worksheet file."
        );
      } finally {
        if (!controller.signal.aborted) {
          setIsLeftPanelViewerLoading(false);
        }
      }
    };

    fetchAssets();

    return () => {
      controller.abort();
      if (leftPanelViewerAbortControllerRef.current === controller) {
        leftPanelViewerAbortControllerRef.current = null;
      }
    };
  }, [
    evaluation?.requestId,
    worksheetEvaluationStatusEndpointBase,
    showWorksheetPreviewPanel,
  ]);

  useEffect(() => {
    return () => {
      if (leftPanelViewerAbortControllerRef.current) {
        leftPanelViewerAbortControllerRef.current.abort();
        leftPanelViewerAbortControllerRef.current = null;
      }
    };
  }, []);

  const handleRetry = useCallback(() => {
    setFetchVersion((prev) => prev + 1);
  }, []);

  const handleLeftResizerMouseDown = useCallback(
    (event) => {
      if (isMobile) {
        return;
      }
      event.preventDefault();
      setIsLeftPanelResizing(true);
    },
    [isMobile]
  );

  const handleRightResizerMouseDown = useCallback(
    (event) => {
      if (isMobile || !showSidePanel) {
        return;
      }
      event.preventDefault();
      setIsRightPanelResizing(true);
    },
    [isMobile, showSidePanel]
  );

  const toggleQuestion = useCallback((questionId) => {
    setExpandedQuestions((prev) => {
      const next = new Set(prev);
      if (next.has(questionId)) {
        next.delete(questionId);
      } else {
        next.add(questionId);
      }
      return next;
    });
  }, []);
  const handleImagePreview = useCallback(
    (imageSrc, imageAlt, images = null, startingIndex = 0) => {
      const normalized = (
        Array.isArray(images) && images.length > 0 ? images : [imageSrc]
      )
        .filter(Boolean)
        .map((src) => {
          if (typeof src === "string") {
            return {
              src,
              isPdf: /\.pdf(\?|$)/i.test(src),
            };
          }
          if (src && typeof src === "object" && typeof src.src === "string") {
            return {
              src: src.src,
              isPdf: Boolean(src.isPdf),
            };
          }
          return { src: String(src || ""), isPdf: false };
        })
        .filter((item) => item.src);

      if (normalized.length === 0) {
        return;
      }

      const safeIndex = Number.isFinite(startingIndex) ? startingIndex : 0;
      setPreviewImages(normalized);
      setPreviewIndex(safeIndex % normalized.length);
      setPreviewImageAlt(imageAlt || "Preview");
      setShowImagePreview(true);
    },
    []
  );

  const goPrevPreview = useCallback(() => {
    setPreviewIndex((prev) => {
      if (previewImages.length <= 1) {
        return 0;
      }
      return (prev - 1 + previewImages.length) % previewImages.length;
    });
  }, [previewImages.length]);

  const goNextPreview = useCallback(() => {
    setPreviewIndex((prev) => {
      if (previewImages.length <= 1) {
        return 0;
      }
      return (prev + 1) % previewImages.length;
    });
  }, [previewImages.length]);

  const selectedSheetQuestions = Array.isArray(evaluation?.questions)
    ? evaluation.questions
    : [];
  const selectedSheetTotalMarks = Number(evaluation?.totalMarks ?? 0);
  const selectedSheetOverallMarks = Number(evaluation?.overallMarks ?? 0);
  const selectedSheetPercentage =
    selectedSheetTotalMarks > 0
      ? Math.round((selectedSheetOverallMarks / selectedSheetTotalMarks) * 100)
      : 0;

  const formattedDate = useMemo(() => {
    if (!evaluation?.date) {
      return "-";
    }
    const parsedDate = new Date(evaluation.date);
    if (Number.isNaN(parsedDate.getTime())) {
      return "-";
    }
    return parsedDate.toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  }, [evaluation?.date]);
  const handleShareResults = useCallback(async () => {
    const requestId = evaluation?.requestId;
    if (!requestId) {
      toast.error("Shareable link is unavailable for this evaluation");
      return;
    }

    const origin =
      typeof window !== "undefined" && window.location?.origin
        ? window.location.origin
        : "";
    const safeRequestId = encodeURIComponent(requestId);
    const shareUrl = origin
      ? `${origin}/evaluateanswersheet/${safeRequestId}`
      : `/evaluateanswersheet/${safeRequestId}`;

    const copyWithFallback = async () => {
      if (
        typeof navigator !== "undefined" &&
        navigator.clipboard &&
        navigator.clipboard.writeText
      ) {
        await navigator.clipboard.writeText(shareUrl);
        return true;
      }

      if (typeof document === "undefined") {
        return false;
      }

      try {
        const textArea = document.createElement("textarea");
        textArea.value = shareUrl;
        textArea.setAttribute("readonly", "");
        textArea.style.position = "fixed";
        textArea.style.top = "-9999px";
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        const successful = document.execCommand("copy");
        document.body.removeChild(textArea);
        return successful;
      } catch (fallbackError) {
        console.error("Fallback copy failed:", fallbackError);
        return false;
      }
    };

    try {
      const copied = await copyWithFallback();
      if (copied) {
        toast.success("Link copied to clipboard");
      } else {
        toast.warn("Unable to copy link automatically");
      }
    } catch (error) {
      console.error("Error copying evaluation link:", error);
      toast.error("Unable to copy link right now");
    }
  }, [evaluation?.requestId]);

  const handleAskDoubt = useCallback(
    (question, stepRow, fallbackStepLabel) => {
      const problemText =
        typeof question?.question_text === "string" &&
        question.question_text.trim().length > 0
          ? question.question_text.trim()
          : typeof question?.extractedContent?.problem === "string"
          ? question.extractedContent.problem.trim()
          : "";

      const answerText =
        typeof question?.answer_text === "string" &&
        question.answer_text.trim().length > 0
          ? question.answer_text.trim()
          : "";

      const stepFeedback =
        typeof stepRow?.feedback === "string" && stepRow.feedback.trim().length
          ? stepRow.feedback.trim()
          : "";

      if (!problemText && !answerText && !stepFeedback) {
        toast.warn("Details for this question are unavailable right now.");
        return;
      }

      const stepLabel =
        typeof stepRow?.step === "number" && Number.isFinite(stepRow.step)
          ? stepRow.step
          : fallbackStepLabel;

      const lines = [];
      if (problemText) {
        lines.push(`For the problem - ${problemText}`);
      }
      if (answerText) {
        lines.push(`and for my solution - ${answerText}`);
      }
      lines.push("");
      if (stepLabel) {
        lines.push(`Why is step ${stepLabel} not correct?`);
      } else {
        lines.push("Why did I lose marks?");
      }
      if (stepFeedback) {
        lines.push(stepFeedback);
      }

      const prefilledText = lines.join("\n");

      if (isMobile) {
        navigate("/doubt", {
          state: {
            prefilledText,
            fromQuestions: true,
          },
        });
        return;
      }

      setAskDoubtText(prefilledText);
      setShowSidePanel(true);
    },
    [isMobile, navigate]
  );

  const renderWorksheetPreviewPanel = (standalone = false) => {
    const pdfUrl =
      typeof leftPanelViewer?.pdfUrl === "string" &&
      leftPanelViewer.pdfUrl.trim().length > 0
        ? leftPanelViewer.pdfUrl.trim()
        : null;

    const imageEntries = Array.isArray(leftPanelViewer?.imageUrls)
      ? leftPanelViewer.imageUrls
          .map((entry) => {
            if (typeof entry === "string") {
              const trimmed = entry.trim();
              return trimmed.length > 0
                ? { src: trimmed, label: "Worksheet page" }
                : null;
            }
            if (
              entry &&
              typeof entry === "object" &&
              typeof entry.src === "string"
            ) {
              return {
                src: entry.src.trim(),
                label:
                  typeof entry.label === "string"
                    ? entry.label.trim()
                    : "Worksheet page",
              };
            }
            return null;
          })
          .filter((entry) => entry && entry.src.length > 0)
      : [];

    const hasImages = imageEntries.length > 0;

    const renderMessage = (message, isError = false) => (
      <div className="flex flex-1 items-center justify-center p-4 text-center text-sm">
        <span className={isError ? "text-red-500" : "text-gray-500"}>
          {message}
        </span>
      </div>
    );

    const openPdfPreview = () => {
      if (!pdfUrl) {
        return;
      }
      handleImagePreview(
        pdfUrl,
        "Worksheet PDF",
        [{ src: pdfUrl, isPdf: true }],
        0
      );
    };

    const renderPdfPreview = () => (
      <div className="flex flex-col h-full overflow-hidden">
        <div className="flex items-center justify-between px-3 py-2 border-b bg-gray-50">
          <button
            type="button"
            onClick={openPdfPreview}
            className="text-xs font-semibold text-[#0D99FF] hover:text-[#5d4de2]"
          >
            Open
          </button>
        </div>
        <div className="flex-1 bg-gray-100 overflow-hidden">
          <iframe
            src={pdfUrl}
            title="Worksheet PDF preview"
            className="w-full h-full border-0"
          />
        </div>
      </div>
    );

    const renderImagesPreview = () => {
      const previewSources = imageEntries.map((entry) => entry.src);
      return (
        <div className="flex flex-col h-full overflow-hidden bg-white">
          <div className="flex items-center gap-2 px-3 py-2 border-b bg-gray-50 text-sm font-semibold text-gray-700">
            <FaImage className="text-[#0D99FF]" />
            <span>Your Answers</span>
          </div>
          <div className="flex-1 overflow-y-auto space-y-3 p-3 bg-white">
            {imageEntries.map(({ src, label }, index) => (
              <button
                key={`${index}-${src}`}
                type="button"
                onClick={() =>
                  handleImagePreview(
                    src,
                    label || `Worksheet page ${index + 1}`,
                    previewSources,
                    index
                  )
                }
                className="w-full border border-gray-200 rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow bg-white text-left"
              >
                <img
                  src={src}
                  alt={`Worksheet page ${index + 1}`}
                  className="w-full h-48 object-contain bg-gray-50"
                  onError={(e) => {
                    e.currentTarget.src = "/images/avatar1.png";
                  }}
                />
                <div className="px-3 py-2 text-xs text-gray-600 border-t border-gray-100">
                  Page {index + 1}
                </div>
              </button>
            ))}
          </div>
        </div>
      );
    };

    const hasRequestId =
      typeof evaluation?.requestId === "string" &&
      evaluation.requestId.trim().length > 0;

    let body = null;
    if (!hasRequestId) {
      body = renderMessage(
        "Worksheet file preview will appear here once available."
      );
    } else if (isLeftPanelViewerLoading) {
      body = (
        <div className="flex flex-1 items-center justify-center">
          <SimpleThreeDotsLoader message="Loading worksheet file..." />
        </div>
      );
    } else if (leftPanelViewerError) {
      body = renderMessage(leftPanelViewerError, true);
    } else if (pdfUrl) {
      body = renderPdfPreview();
    } else if (hasImages) {
      body = renderImagesPreview();
    } else {
      body = renderMessage("No worksheet file available for this evaluation.");
    }

    const panelClassName = standalone
      ? "w-full border border-gray-200 rounded-lg overflow-hidden bg-white flex flex-col"
      : "h-full border-r border-gray-200 bg-white flex flex-col";
    const panelStyle = standalone
      ? undefined
      : {
          width: `${leftPanelWidth}%`,
          minWidth: "220px",
          pointerEvents: isLeftPanelResizing ? "none" : undefined,
        };

    return (
      <div className={panelClassName} style={panelStyle}>
        {body}
      </div>
    );
  };

  const renderEvaluationContent = () => {
    if (!evaluation) {
      return null;
    }

    return (
      <div className="space-y-4 w-[90%] mx-auto">
        <div className="flex items-center justify-between px-4 py-1 border-2 border-[#0D99FF]/20 rounded-lg bg-gradient-to-r from-[#0D99FF]/5 to-[#0D99FF]/10 shadow-sm flex-wrap gap-4">
          <div className="flex-1 min-w-[200px]">
            <h2 className="text-lg font-bold text-gray-800 mb-0.5">
              {evaluation.fileName || "Worksheet Evaluation"}
            </h2>
            <p className="text-xs text-gray-600">
              {evaluation.subject
                ? `${evaluation.subject} Evaluation`
                : "Evaluation Summary"}
            </p>
            <p className="text-xs text-gray-500 mt-1">{formattedDate}</p>
          </div>
          <div className="text-center">
            <p className="text-xs font-medium text-gray-600 mb-0">
              Overall Score
            </p>
            <div className="flex items-baseline justify-center gap-0.5">
              <span className="text-lg font-bold text-[#0D99FF]">
                {Number.isFinite(selectedSheetOverallMarks)
                  ? selectedSheetOverallMarks
                  : 0}
              </span>
              <span className="text-sm text-gray-500">/</span>
              <span className="text-base font-semibold text-gray-700">
                {Number.isFinite(selectedSheetTotalMarks)
                  ? selectedSheetTotalMarks
                  : 0}
              </span>
            </div>
            <div className="rounded-full text-center">
              <span className="text-sm font-semibold text-[#0D99FF]">
                {` ( ${selectedSheetPercentage}% )`}
              </span>
            </div>
          </div>
        </div>

        {showWorksheetPreviewPanel && isMobile && (
          <div className="space-y-2">
            <p className="text-sm font-semibold text-gray-700">
              Worksheet file preview
            </p>
            {renderWorksheetPreviewPanel(true)}
          </div>
        )}

        <div className="space-y-4">
          {selectedSheetQuestions.length > 0 ? (
            selectedSheetQuestions.map((question, index) => {
              const questionId = question?.id ?? index + 1;
              const isExpanded = expandedQuestions.has(questionId);
              const marksAwarded = Number(question?.marks ?? 0);
              const totalMarks = Number(question?.totalMarks ?? 0);
              const formattedMarks = Number.isFinite(marksAwarded)
                ? marksAwarded.toFixed(1)
                : (0).toFixed(1);
              const formattedTotal =
                Number.isFinite(totalMarks) && totalMarks > 0
                  ? Number.isInteger(totalMarks)
                    ? totalMarks.toString()
                    : totalMarks.toFixed(1)
                  : "0";
              const normalizedAnswerImages = Array.isArray(
                question?.answerImages
              )
                ? question.answerImages
                : [];
              const questionImageSrc =
                typeof question?.questionImage === "string"
                  ? question.questionImage
                  : question?.questionImage
                  ? URL.createObjectURL(question.questionImage)
                  : null;

              return (
                <div
                  key={questionId}
                  className="border-2 border-[#0D99FF]/20 rounded-lg shadow-sm bg-white"
                >
                  <div
                    className="flex items-center justify-between p-3 bg-white border-b border-[#0D99FF]/20 rounded-t-lg cursor-pointer hover:bg-[#0D99FF]/5 transition-colors"
                    onClick={() => toggleQuestion(questionId)}
                  >
                    <div className="flex items-center gap-2">
                      {isExpanded ? (
                        <FaChevronDown className="w-4 h-4 text-[#0D99FF]" />
                      ) : (
                        <FaChevronRight className="w-4 h-4 text-[#0D99FF]" />
                      )}
                      <h3 className="text-lg font-semibold text-[#0D99FF]">
                        Question {index + 1}
                      </h3>
                    </div>
                    <div className="text-right">
                      <span className="text-lg font-bold text-[#0D99FF]">
                        {formattedMarks}/{formattedTotal}
                      </span>
                      <p className="text-xs text-[#0D99FF]/70">Score</p>
                    </div>
                  </div>

                  {isExpanded && (
                    <div className="p-4">
                      <div className="flex flex-col md:flex-row gap-3 mb-3">
                        {questionImageSrc && (
                          <div className="md:w-1/3 w-full flex-shrink-0">
                            <img
                              src={questionImageSrc}
                              alt={`Question ${index + 1}`}
                              className="w-full h-40 object-contain border rounded-lg bg-gray-50 cursor-zoom-in"
                              onClick={() =>
                                handleImagePreview(
                                  questionImageSrc,
                                  `Question ${index + 1}`,
                                  [questionImageSrc],
                                  0
                                )
                              }
                            />
                          </div>
                        )}
                        <div className={questionImageSrc ? "flex-1" : "w-full"}>
                          <p className="text-gray-700 font-medium text-sm mb-2">
                            Question Content:
                          </p>
                          <div className="bg-gray-100 border border-gray-200 p-3 rounded-lg">
                            <p className="text-sm text-gray-800 mb-2">
                              <strong>Problem:</strong>{" "}
                              <MathJaxRenderer className="inline">
                                {applyBasicMarkdown(
                                  question?.question_text ||
                                    question?.extractedContent?.problem ||
                                    "Question content not available."
                                )}
                              </MathJaxRenderer>
                            </p>
                          </div>
                        </div>
                      </div>
                      <div className="mb-3 space-y-3">
                        {normalizedAnswerImages.length > 0 && (
                          <div>
                            <p className="text-gray-700 font-medium text-sm mb-1">
                              Your Answer Images:
                            </p>
                            <div className="flex overflow-x-auto gap-2 p-2 border border-gray-200 rounded-lg bg-white">
                              {normalizedAnswerImages.map((img, imgIndex) => {
                                const imageSrc =
                                  typeof img === "string"
                                    ? img
                                    : img && typeof img === "object" && img.src
                                    ? img.src
                                    : "";
                                if (!imageSrc) {
                                  return null;
                                }
                                return (
                                  <img
                                    key={imgIndex}
                                    src={imageSrc}
                                    alt={`Answer ${imgIndex + 1}`}
                                    className="h-28 w-auto object-contain border rounded-lg flex-shrink-0 cursor-zoom-in hover:shadow-lg transition-shadow"
                                    onClick={() =>
                                      handleImagePreview(
                                        imageSrc,
                                        `Answer ${imgIndex + 1} - Question ${
                                          index + 1
                                        }`,
                                        normalizedAnswerImages,
                                        imgIndex
                                      )
                                    }
                                    onError={(e) => {
                                      e.currentTarget.src =
                                        "/images/avatar1.png";
                                    }}
                                  />
                                );
                              })}
                            </div>
                          </div>
                        )}

                        {(question?.answer_text ||
                          normalizedAnswerImages.length === 0) && (
                          <div>
                            <p className="text-gray-700 font-medium text-sm mb-1">
                              Your Answer:
                            </p>
                            <div className="bg-gray-100 border border-gray-200 p-3 rounded-lg">
                              <div className="text-sm text-gray-800">
                                <MathJaxRenderer className="whitespace-pre-line">
                                  {applyBasicMarkdown(
                                    question?.answer_text ||
                                      "Answer not available."
                                  )}
                                </MathJaxRenderer>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>

                      <div className="bg-white border border-gray-200 p-4 rounded-lg shadow-sm">
                        <div className="flex items-center justify-between mb-3">
                          <p className="text-gray-800 font-medium text-sm">
                            Feedback & Analysis:
                          </p>
                        </div>
                        <p className="text-[#0D99FF] font-semibold text-base">
                          Question score : {formattedMarks}/{formattedTotal}
                        </p>
                        <div className="text-sm text-gray-700 mb-4 leading-relaxed">
                          <MathJaxRenderer className="leading-relaxed">
                            {applyBasicMarkdown(
                              question?.feedback ||
                                "Detailed feedback will appear here once evaluation is complete."
                            )}
                          </MathJaxRenderer>
                        </div>

                        {Array.isArray(question?.stepBreakdown) &&
                          question.stepBreakdown.length > 0 && (
                            <div className="mb-2">
                              <h4 className="text-sm font-semibold text-gray-800 mb-2">
                                Step-by-Step Evaluation:
                              </h4>
                              <div className="overflow-x-auto">
                                <table className="w-full text-sm">
                                  <thead>
                                    <tr className="bg-gray-50 border-b border-gray-200">
                                      <th className="text-left py-2 px-3 font-medium text-gray-700 w-20 min-w-[80px]">
                                        Step
                                      </th>
                                      <th className="text-left py-2 px-3 font-medium text-gray-700">
                                        Feedback
                                      </th>
                                      <th className="text-right py-2 px-3 font-medium text-gray-700">
                                        Marks
                                      </th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {question.stepBreakdown.map(
                                      (row, rowIndex) => (
                                        <tr
                                          key={rowIndex}
                                          className="border-b border-gray-100"
                                        >
                                          <td className="py-2 px-3 text-gray-600 font-medium w-20 min-w-[80px] whitespace-nowrap">
                                            Step {row?.step ?? rowIndex + 1}
                                          </td>
                                          <td className="py-2 px-3 text-gray-700 align-top">
                                            <FeedbackContent
                                              row={row}
                                              onAskDoubt={() =>
                                                handleAskDoubt(
                                                  question,
                                                  row,
                                                  row?.step ?? rowIndex + 1
                                                )
                                              }
                                            />
                                          </td>
                                          <td className="py-2 px-3 text-right text-gray-800 font-semibold align-top">
                                            {Number.isFinite(Number(row?.marks))
                                              ? Number(row?.marks).toFixed(1)
                                              : "0.0"}
                                            /
                                            {Number.isFinite(
                                              Number(row?.maxMarks)
                                            )
                                              ? Number(row?.maxMarks).toFixed(1)
                                              : "0.0"}
                                          </td>
                                        </tr>
                                      )
                                    )}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })
          ) : (
            <div className="py-12 text-center text-sm text-gray-500 border border-dashed border-gray-300 rounded-lg bg-white">
              No questions available for this evaluation yet.
            </div>
          )}
        </div>
      </div>
    );
  };
  const evaluationContent = renderEvaluationContent();

  let mainContent;
  if (isLoading) {
    mainContent = (
      <div className="flex-1 overflow-y-auto p-6 pb-32 bg-gray-50">
        <div className="py-16 flex flex-col items-center">
          <SimpleThreeDotsLoader message="Loading evaluation result..." />
        </div>
      </div>
    );
  } else if (error) {
    mainContent = (
      <div className="flex-1 overflow-y-auto p-6 pb-32 bg-gray-50">
        <div className="py-14 text-center space-y-4">
          <p className="text-base font-medium text-red-600">{error}</p>
          <button
            type="button"
            onClick={handleRetry}
            className="inline-flex items-center px-4 py-2 bg-[#0D99FF] text-white rounded-lg hover:bg-[#5d4de2] transition-colors"
          >
            Try again
          </button>
        </div>
      </div>
    );
  } else if (evaluation) {
    if (isMobile) {
      mainContent = (
        <div className="flex-1 overflow-y-auto p-6 pb-32 bg-gray-50">
          {evaluationContent}
        </div>
      );
    } else {
      mainContent = (
        <div className="flex h-full min-h-0" ref={layoutRef}>
          {/* showWorksheetPreviewPanel */}
          { showWorksheetPreviewPanel && (
            <>
              {renderWorksheetPreviewPanel()}
              <div
                className="w-4 h-full flex flex-col justify-center items-center cursor-col-resize"
                onMouseDown={handleLeftResizerMouseDown}
                title="Drag to resize"
              >
                <div className="flex-grow w-1 bg-gray-300 transition-colors duration-200" />
                <div className="p-1 rounded-md bg-white border border-gray-400 shadow-md transition-all duration-200 hover:bg-gray-100">
                  <div className="grid grid-cols-2 gap-x-1 gap-y-0.5">
                    <div className="w-1 h-1 bg-gray-700 rounded-full" />
                    <div className="w-1 h-1 bg-gray-700 rounded-full" />
                    <div className="w-1 h-1 bg-gray-700 rounded-full" />
                    <div className="w-1 h-1 bg-gray-700 rounded-full" />
                    <div className="w-1 h-1 bg-gray-700 rounded-full" />
                    <div className="w-1 h-1 bg-gray-700 rounded-full" />
                  </div>
                </div>
                <div className="flex-grow w-1 bg-gray-300 transition-colors duration-200" />
              </div>
            </>
          )}
          <div className="flex flex-1 min-h-0" ref={centerLayoutRef}>
            <div
              className={`flex flex-col min-h-0 ${
                showSidePanel ? "border-r border-gray-200" : ""
              }`}
              style={{
                width: showSidePanel ? `${centerPanelWidth}%` : "100%",
              }}
            >
              <div className="flex-1 overflow-y-auto p-6 pb-32 bg-gray-50">
                {evaluationContent}
              </div>
            </div>
            {showSidePanel && (
              <>
                <div
                  className="w-4 h-full flex flex-col justify-center items-center cursor-col-resize"
                  onMouseDown={handleRightResizerMouseDown}
                  title="Drag to resize"
                >
                  <div className="flex-grow w-1 bg-gray-300 transition-colors duration-200" />
                  <div className="p-1 rounded-md bg-white border border-gray-400 shadow-md transition-all duration-200 hover:bg-gray-100">
                    <div className="grid grid-cols-2 gap-x-1 gap-y-0.5">
                      <div className="w-1 h-1 bg-gray-700 rounded-full" />
                      <div className="w-1 h-1 bg-gray-700 rounded-full" />
                      <div className="w-1 h-1 bg-gray-700 rounded-full" />
                      <div className="w-1 h-1 bg-gray-700 rounded-full" />
                      <div className="w-1 h-1 bg-gray-700 rounded-full" />
                      <div className="w-1 h-1 bg-gray-700 rounded-full" />
                    </div>
                  </div>
                  <div className="flex-grow w-1 bg-gray-300 transition-colors duration-200" />
                </div>
                <div
                  className="flex flex-col min-h-0 bg-white"
                  style={{
                    width: `${100 - centerPanelWidth}%`,
                  }}
                >
                  <DoubtSolver
                    disableWorkbookMode={true}
                    prefilledTextProp={askDoubtText}
                    autoSendPrefilledText={false}
                  />
                </div>
              </>
            )}
          </div>
        </div>
      );
    }
  } else {
    mainContent = (
      <div className="flex-1 overflow-y-auto p-6 pb-32 bg-gray-50">
        <div className="py-14 text-center text-gray-600">
          No evaluation data is available for this sheet.
        </div>
      </div>
    );
  }

  return (
    <MathJaxContext config={mathJaxConfig}>
      <div className="min-h-screen bg-gray-50 flex flex-col relative">
        <div className="flex flex-1 overflow-hidden">
          <div className="flex flex-col flex-1 bg-gray-50 relative">
            <div className="absolute top-4 right-4 flex items-center gap-2 z-10">
              <button
                onClick={() => {
                  if (!isMobile && showSidePanel) {
                    setShowSidePanel(false);
                    setAskDoubtText("");
                    return;
                  }
                  navigate("/evaluateanswersheet");
                }}
                className="text-[#0D99FF] hover:text-[#5d4de2] transition-colors p-2 rounded-full hover:bg-[#0D99FF]/10"
                aria-label="Close results"
              >
                <FaTimes className="w-5 h-5" />
              </button>
            </div>
            <div className="flex-1 bg-gray-50 relative">{mainContent}</div>
            {evaluation && (
              <div className="fixed bottom-6 right-6 z-50">
                <button
                  type="button"
                  onClick={handleShareResults}
                  className="flex items-center gap-2 px-6 py-3 bg-[#0D99FF] text-white rounded-full shadow-xl hover:bg-[#5d4de2] focus:outline-none focus:ring-2 focus:ring-[#0D99FF]/50 transition-colors"
                >
                  <FaShareAlt className="w-4 h-4" />
                  <span className="text-sm font-semibold tracking-wide">
                    Share with friends
                  </span>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
      {showImagePreview && previewImages.length > 0 && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] flex flex-col overflow-hidden shadow-2xl relative">
            <div className="flex items-center justify-between p-4 border-b bg-[#0D99FF] text-white">
              <h3 className="text-lg font-semibold">{previewImageAlt}</h3>
              <button
                type="button"
                onClick={() => setShowImagePreview(false)}
                className="p-1 rounded-full hover:bg-white hover:bg-opacity-20 transition-colors"
                aria-label="Close preview"
              >
                <FaTimes className="w-5 h-5" />
              </button>
            </div>
            <div className="flex-1 overflow-auto p-4 bg-gray-50 relative">
              {previewImages.length > 1 && (
                <button
                  type="button"
                  aria-label="Previous image"
                  onClick={goPrevPreview}
                  className="absolute left-4 top-1/2 -translate-y-1/2 bg-white bg-opacity-90 hover:bg-opacity-100 text-[#0D99FF] rounded-full p-2 shadow"
                >
                  <FaChevronLeft className="w-6 h-6" />
                </button>
              )}
              {(() => {
                const current = previewImages[previewIndex] || {};
                if (current.isPdf) {
                  return (
                    <iframe
                      src={current.src}
                      title={previewImageAlt}
                      className="w-full h-[70vh] rounded-lg border shadow-lg bg-white"
                    />
                  );
                }
                return (
                  <img
                    src={current.src}
                    alt={previewImageAlt}
                    className="w-full h-auto max-h-[70vh] object-contain rounded-lg shadow-lg"
                    onError={(e) => {
                      e.currentTarget.src = "/images/avatar1.png";
                    }}
                  />
                );
              })()}
              {previewImages.length > 1 && (
                <button
                  type="button"
                  aria-label="Next image"
                  onClick={goNextPreview}
                  className="absolute right-4 top-1/2 -translate-y-1/2 bg-white bg-opacity-90 hover:bg-opacity-100 text-[#0D99FF] rounded-full p-2 shadow"
                >
                  <FaChevronRightIcon className="w-6 h-6" />
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </MathJaxContext>
  );
};

export default PublicEvaluateAnswerSheet;
